import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useCurrency } from '../../hooks/useCurrency';
import { ExpenseBreakdownData } from '../../hooks/useChartsData';

interface ExpensesPieChartProps {
  data: ExpenseBreakdownData[];
  formatAmount?: (amount: number) => string;
}

export function ExpensesPieChart({ data, formatAmount }: ExpensesPieChartProps) {
  const { currentCurrency } = useCurrency();
  
  const defaultFormatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-CD', { 
      style: 'currency', 
      currency: currentCurrency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const amountFormatter = formatAmount || defaultFormatAmount;
  
  // Custom label function pour afficher les pourcentages
  const renderCustomLabel = ({ 
    cx, 
    cy, 
    midAngle, 
    innerRadius, 
    outerRadius, 
    percent 
  }: any) => {
    if (percent < 0.05) return null; // Ne pas afficher les labels pour les très petites portions
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="600"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={350}>
      <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderCustomLabel}
          outerRadius={100}
          innerRadius={40}
          fill="#8884d8"
          paddingAngle={2}
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color}
              stroke={entry.color}
              strokeWidth={1}
            />
          ))}
        </Pie>
        
        <Tooltip 
          formatter={(value: number, name: string) => [
            amountFormatter(value),
            name
          ]}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />
        
        <Legend 
          verticalAlign="bottom"
          height={36}
          wrapperStyle={{ 
            fontSize: '12px', 
            paddingTop: '20px'
          }}
          formatter={(value, entry: any) => (
            <span style={{ color: entry.color }}>
              {value} ({entry.payload?.percentage?.toFixed(1)}%)
            </span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}